import type { AvatarProps } from "../components/avatar/Avatar";
import type { Group } from "../state/group";
import type { Invite } from "../state/invite";
import type { User } from "../state/user";
import { resolveInitials as resolveGroupInitials } from "./groupUtil";
import { isInvite, resolveInitials as resolveUserInitials, resolveInitialsFromEmail } from "./userUtil";
import type UserId from "../state/UserId.ts";
import type InviteId from "../state/InviteId.ts";
import type GroupId from "../state/GroupId.ts";
import type { AvatarOption } from "../types/Menu.ts";
import { isValidObjectId } from "./objectIdUtil.ts";

export type EntityAvatar = Pick<AvatarProps, "shape" | "backgroundColor"> & { initials: string };

export const groupAvatar = (group: Group): EntityAvatar => ({
  initials: resolveGroupInitials(group),
  shape: "square",
  backgroundColor: resolveColor(group.id),
});

const userAvatar = (user: User | Invite): EntityAvatar => ({
  initials: resolveUserInitials(user),
  shape: "circle",
  backgroundColor: resolveColor(user.id),
});

const inviteAvatar = (invite: Pick<Invite, "id" | "emailAddress">): EntityAvatar => ({
  initials: resolveInitialsFromEmail(invite.emailAddress),
  shape: "circle",
  backgroundColor: resolveColor(invite.id),
});

export const memberAvatar = (member: User | Invite): EntityAvatar =>
  isInvite(member) ? inviteAvatar(member) : userAvatar(member);

const avatarColors: AvatarProps["backgroundColor"][] = ["brand", "blue", "purple", "pink", "gray", "orange", "green"];
const resolveColor = (id: UserId | InviteId | GroupId): AvatarProps["backgroundColor"] => {
  if (!isValidObjectId(id.toString())) {
    throw new Error(`Invalid object id; ${id}`);
  }

  return avatarColors[Number(BigInt(`0x${id}`) % BigInt(avatarColors.length))];
};

type SelectOptionValue<T> = T extends User ? { kind: "user"; id: UserId } : { kind: "invite"; id: InviteId };
export const toSelectOption = <T extends User | Invite>(entity: T): AvatarOption<SelectOptionValue<T>> => ({
  variant: "avatar",
  ...memberAvatar(entity),
  label: entity.emailAddress,
  value: isInvite(entity)
    ? ({ kind: "invite" as const, id: entity.id } as SelectOptionValue<T>)
    : ({ kind: "user" as const, id: entity.id } as SelectOptionValue<T>),
});
